import React from "react";
import { Button } from "components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { Flex, SkeletonCircle, Stack } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import BadgeComp from "components/RFQ/BadgeComp/BadgeComp";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface TabMenuItem {
	id: string;
	name: string;
	disabled?: boolean;
	isValid?: boolean;
	showBadge?: boolean;
	testId?: string;
	total?: number;
}

export interface TabComponentProps {
	activeTab?: string;
	enableValidation?: boolean;
	isLoading?: boolean;
	menus: TabMenuItem[];
	changeActiveTab: (name: string, id: string) => void;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const TabComponent: React.FC<TabComponentProps> = ({
	menus,
	activeTab,
	changeActiveTab,
	isLoading = false,
	enableValidation = false,
}) => {
	const clickTab = (name: string, index: number, id: string): void => {
		if (enableValidation) {
			let enableTab = true;

			for (let a = 0; a < index; a++) {
				if (!menus[a]?.isValid) {
					enableTab = false;
					break;
				}
			}

			if (enableTab) {
				changeActiveTab(name, id);
			}
		} else {
			changeActiveTab(name, id);
		}
	};

	return (
		<>
			{isLoading ? (
				<Flex justify="space-between">
					<Flex gap="8px">
						{menus.map((_, index) => (
							<div key={index} className="flex">
								<div className="mr-2">
									<SkeletonBase width="100px" height="16px" />
								</div>
								<div className="mr-4">
									<SkeletonCircle
										width={5}
										height={5}
										startColor="lightgray"
									/>
								</div>
							</div>
						))}
					</Flex>
				</Flex>
			) : (
				<Stack
					direction={{ base: "column", sm: "row" }}
					w="full"
					gap={2}
					align={{ base: "stretch", md: "center" }}
					wrap="wrap"
					className="pb-4"
				>
					{menus.map((tbMenu, index) =>
						tbMenu.disabled ? null : (
							<div key={tbMenu.id ?? index}>
								{/* @ts-ignore */}
								<Button
									id={`tabs-${index}`}
									data-testid={tbMenu.testId}
									btntype="tab"
									active={
										activeTab === tbMenu.name ||
										activeTab === tbMenu.id
									}
									onClick={() =>
										clickTab(tbMenu.name, index, tbMenu.id)
									}
								>
									<div
										style={{
											fontFamily: "roboto",
											fontWeight: 500,
											color:
												activeTab === tbMenu.name ||
													activeTab === tbMenu.id
													? "#0031DD"
													: "#667085",
										}}
									>
										{tbMenu.name}
									</div>

									{tbMenu.showBadge && (
										<BadgeComp
											title={tbMenu.name}
											active={activeTab}
											total={tbMenu.total}
										/>
									)}

									{enableValidation && (
										<div className="ml-2">
											{tbMenu.isValid ? (
												<Icon icon="tick" />
											) : (
												<Icon icon="toast-error" />
											)}
										</div>
									)}
								</Button>
							</div>
						)
					)}
				</Stack>
			)}
		</>
	);
};